// Ingredientes.jsx
import React from 'react';
import {
  PageWrapper,
  HeroSection,
  ContentSection,
  TextBlock,
  StatementHeading,
  FullWidthImage,
  TwoColImages,
} from './PageTemplate';
import ProductBlock from '../../components/ProductBlock';
import SEO from '../../components/SEO';

const IMAGES = {
  fullWidth1: '/images/secciones/curated-selection/Portada_sostenibilidad_physiology_skincare_cosmetic_ingredientes_mx_2.webp',
  fullWidth2: '/images/secciones/curated-selection/Portada_sostenibilidad_physiology_skincare_cosmetic_ingredientes_mx_3.webp',
  // Imagen del final de la página (antes repetía fullWidth1, la del inicio).
  fullWidth3: '/images/secciones/curated-selection/Imagen_temporal_seccion_2.webp',
  grid: ['/images/secciones/curated-selection/Triada_antimanchas_niacinamida_retinol_physiology_skincare_2.webp', '/images/secciones/curated-selection/Bha_serum_physiology_skincare.webp'],
};

const PRODUCTS = [
  {
    id: 'ingr-prod1',
    title: 'Vitamina C Pura - Drop 30 mL',
    description: 'Aporta luminosidad con antioxidantes',
    price: 750,
    image: 'https://pub-af8345b2339e47c4a9067290386af123.r2.dev/vitamina_12_Serum_luminosidad_physiology_skincare_12%25-01KWV2NHKDD8YNKY1CVE8DJ00X.webp',
    thumbnail: 'https://pub-af8345b2339e47c4a9067290386af123.r2.dev/vitamina_12_Serum_luminosidad_physiology_skincare_12%25-01KWV2NHKDD8YNKY1CVE8DJ00X.webp',
    handle: 'vitamina-c-pura',
    variants: [{ id: 'v1', title: '2 Porcentajes', label: '2 Porcentajes', price: 750 }],
    inStock: true,
    badge: 'essential',
  },
  {
    id: 'ingr-prod2',
    title: 'Tx10/5 Azelaico + Niacinamide',
    description: 'Ayuda en manchas, rosácea y acné',
    price: 600,
    originalPrice: 700,
    image: 'https://pub-af8345b2339e47c4a9067290386af123.r2.dev/Niacinamida%204%2030%20ml%20n-01KVN2GE4Z5FWFYASXVYXKZCSH-01KWTVPDFDE8ZA6P5TF86NKX6X.webp',
    thumbnail: 'https://pub-af8345b2339e47c4a9067290386af123.r2.dev/Niacinamida%204%2030%20ml%20n-01KVN2GE4Z5FWFYASXVYXKZCSH-01KWTVPDFDE8ZA6P5TF86NKX6X.webp',
    handle: 'tx10-azelaico-niacinamide',
    variants: [{ id: 'v2', title: '40 ml', label: '40 ml', price: 600 }],
    inStock: true,
  },
];

const Ingredientes = () => {
  return (
    <PageWrapper>
      <SEO
        title="Ingredientes — Activos de alta pureza y trazabilidad"
        description="Conoce los ingredientes clave de Physiology Skincare Co: niacinamida, vitamina C, ácido hialurónico, prebióticos y activos botánicos de alta pureza."
        path="/ingredientes"
      />

      {/* ── Hero ── */}
      <HeroSection
        category="Investigación crítica"
        title="Ingredientes eficaces para piel que exige resultados reales"
        meta="Tiempo de lectura: 10 min"
      />

      {/* ── Imagen full-width ── */}
      <FullWidthImage src={IMAGES.fullWidth1} alt="Ingredientes Physiology" />

      {/* ── Sección 1 ── */}
      <ContentSection narrow>
        <TextBlock
          heading="Desde moléculas que inhiben la melanina hasta exfoliantes de nueva generación"
          body="Existen varios ingredientes activos que han demostrado ser efectivos en el tratamiento del tono desigual de la piel. Estos ingredientes abordan diferentes aspectos del problema, desde la inhibición enzimática hasta la renovación celular acelerada. Aquí hay algunos ingredientes que usamos y cómo funcionan:"
        />
        <TextBlock
          heading="Vitamina C (Ácido L-ascórbico) — el estándar de referencia"
          body="A concentraciones entre 10% y 20%, el ácido L-ascórbico inhibe la tirosinasa, la enzima responsable de la síntesis de melanina. Su inestabilidad es su principal desafío — por eso nuestras fórmulas trabajan con sistemas de encapsulación que preservan su potencia hasta el momento de la aplicación."
        />
      </ContentSection>

      {/* ── Statement ── */}
      <ContentSection narrow>
        <StatementHeading text="Physiology traduce la bioquímica en resultados visibles:" />
        <TextBlock
          heading="Niacinamida — acción múltiple, tolerabilidad excepcional"
          body="La vitamina B3 en su forma activa trabaja en varios frentes simultáneamente: reduce la transferencia de melanosomas a los queratinocitos, fortalece la barrera lipídica y regula la producción de sebo. A 10% es uno de los activos con mejor perfil de tolerabilidad disponibles, incluso en pieles sensibles o comprometidas."
        />
        <TextBlock
          heading="Ácido tranexámico — el activo que llegó de la medicina"
          body="Originalmente desarrollado para reducir el sangrado quirúrgico, el ácido tranexámico demostró en estudios posteriores una capacidad significativa para reducir melasma y manchas de origen hormonal. Actúa en la comunicación entre melanocitos y queratinocitos, interrumpiendo la señal que activa la hiperpigmentación."
        />
      </ContentSection>

      {/* ── Grid imágenes ── */}
      <ContentSection>
        <TwoColImages images={IMAGES.grid} />
      </ContentSection>

      {/* ── Más texto ── */}
      <ContentSection narrow>
        <TextBlock
          heading="Alpha Arbutin — estabilidad donde la Vitamina C no llega"
          body="El alpha arbutin libera hidroquinona de forma gradual y controlada directamente en la epidermis, inhibiendo la tirosinasa con un perfil de seguridad considerablemente mejor que la hidroquinona libre. Es nuestra opción preferida para pieles con hiperpigmentación profunda que requieren tratamiento sostenido."
        />
        <TextBlock
          heading="Retinoides — renovación desde la base"
          body="El retinol y sus derivados aceleran el turnover celular, llevando a la superficie células nuevas con menor carga de pigmento. La clave está en la velocidad de introducción y la concentración: comenzar a 0.1% y escalar gradualmente según respuesta cutánea es el protocolo que evita el eritema de adaptación."
        />
      </ContentSection>

      {/* ── Imagen full-width ── */}
      <FullWidthImage src={IMAGES.fullWidth2} alt="Laboratorio ingredientes" />

      {/* ── Más texto ── */}
      <ContentSection narrow>
        <TextBlock
          heading="Exfoliantes químicos — AHA y PHA para superficies más uniformes"
          body="Los alfa-hidroxiácidos como el ácido glicólico y el láctico actúan en la cohesión corneocítica, acelerando la descamación de células superficiales pigmentadas. Los PHA, de molécula más grande, logran el mismo efecto con menor potencial irritante — ideales para fototipos más altos donde el riesgo de hiperpigmentación post-inflamatoria es mayor."
        />
        <TextBlock
          heading="Péptidos de señalización — la nueva frontera"
          body="Los péptidos biomimeticos de tercera generación actúan sobre los receptores de melanocorticina, modulando la respuesta pigmentaria directamente en el melanocito. Son activos con una ventana de efectividad más estrecha pero con capacidad de acción en vías que los inhibidores tradicionales no alcanzan."
        />
      </ContentSection>

      {/* ── Productos relacionados ── */}
      <ProductBlock
        title="Productos relacionados"
        description="Ingredientes activos en acción"
        buttonText="Ver todos"
        products={PRODUCTS}
      />

      {/* ── Imagen cierre ── */}
      <FullWidthImage src={IMAGES.fullWidth3} alt="Ingredientes en uso" />

      {/* ── Cierre ── */}
      <ContentSection narrow>
        <TextBlock
          heading="El protocolo importa tanto como el ingrediente"
          body="Un activo eficaz aplicado en el orden incorrecto, a un pH incompatible o en combinación con un antagonista pierde una fracción significativa de su potencia. En nuestra sección de rutinas detallamos los protocolos de uso que maximizan la efectividad de cada ingrediente sin comprometer la tolerabilidad."
        />
        <TextBlock
          heading="Concentración publicada, no estimada"
          body="Cada producto de la línea de tono desigual especifica la concentración exacta de sus activos principales. No porque sea una exigencia regulatoria — sino porque es la única forma de que puedas comparar con criterio lo que estás usando versus cualquier alternativa del mercado."
        />
      </ContentSection>

    </PageWrapper>
  );
};

export default Ingredientes;
